import {
    DeleteOutlined,
    EditOutlined,
    ForwardToInboxOutlined,
    PeopleOutlined,
} from '@mui/icons-material';
import {
    Box,
    Chip,
    IconButton,
    Paper,
    Skeleton,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tooltip,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';

import Result from 'components/v2/common/Result';

import type { ImmigrationProfessional } from 'api';
import { ACTIONS } from 'constant';

interface PermissionsTableProps {
    users: ImmigrationProfessional[];
    loading?: boolean;
    onAction: (action: ACTIONS, user: ImmigrationProfessional) => void;
}

const formatRole = (role: string) =>
    role
        .split('_')
        .filter(Boolean)
        .map((part) => part.charAt(0) + part.slice(1).toLowerCase())
        .join(' ');

const getFullName = (user: ImmigrationProfessional) =>
    [user.firstname, user.middlename, user.lastname].filter(Boolean).join(' ') ||
    `User #${user.id}`;

function StatusChip({ status }: { status: string }) {
    const isInvited = status === 'INVITED';

    return (
        <Chip
            size="small"
            label={isInvited ? 'Invited' : 'Confirmed'}
            sx={{
                height: 22,
                fontSize: '0.75rem',
                fontWeight: 600,
                borderRadius: 1,
                bgcolor: isInvited ? 'rgba(245, 158, 11, 0.12)' : 'rgba(34, 197, 94, 0.12)',
                color: isInvited ? '#b45309' : '#15803d',
                '& .MuiChip-label': { px: 1 },
            }}
        />
    );
}

const actionIconButtonSx = {
    p: 0.5,
};

function RowActions({
    user,
    onAction,
}: {
    user: ImmigrationProfessional;
    onAction: (action: ACTIONS, user: ImmigrationProfessional) => void;
}) {
    return (
        <Stack direction="row" spacing={0.25} sx={{ flexWrap: 'wrap', justifyContent: 'flex-end' }}>
            {user.status === 'INVITED' ? (
                <Tooltip title="Resend invitation">
                    <span>
                        <IconButton
                            size="small"
                            color="primary"
                            aria-label="Resend invitation"
                            sx={actionIconButtonSx}
                            onClick={() => onAction(ACTIONS.RESEND, user)}
                        >
                            <ForwardToInboxOutlined sx={{ fontSize: 18 }} />
                        </IconButton>
                    </span>
                </Tooltip>
            ) : null}

            <Tooltip title="Edit">
                <span>
                    <IconButton
                        size="small"
                        aria-label="Edit collaborator"
                        sx={actionIconButtonSx}
                        onClick={() => onAction(ACTIONS.EDIT, user)}
                    >
                        <EditOutlined sx={{ fontSize: 18 }} />
                    </IconButton>
                </span>
            </Tooltip>

            <Tooltip title="Delete">
                <span>
                    <IconButton
                        size="small"
                        color="error"
                        aria-label="Delete collaborator"
                        sx={actionIconButtonSx}
                        onClick={() => onAction(ACTIONS.DELETE, user)}
                    >
                        <DeleteOutlined sx={{ fontSize: 18 }} />
                    </IconButton>
                </span>
            </Tooltip>
        </Stack>
    );
}

export function PermissionsTable({ users, loading = false, onAction }: PermissionsTableProps) {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    const denseCellSx = {
        py: 0.75,
        px: 1.25,
    };

    if (loading) {
        if (isMobile) {
            return (
                <Stack spacing={1}>
                    {Array.from({ length: 4 }).map((_, index) => (
                        <Paper key={index} variant="outlined" sx={{ p: 1.5, borderRadius: 1.5 }}>
                            <Stack spacing={0.75}>
                                <Skeleton variant="text" width="55%" height={22} />
                                <Skeleton variant="text" width="72%" height={18} />
                                <Skeleton variant="text" width="40%" height={18} />
                                <Skeleton variant="rounded" width={100} height={24} />
                            </Stack>
                        </Paper>
                    ))}
                </Stack>
            );
        }

        return (
            <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 1.5 }}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            {['Name', 'Email', 'Status', 'Role', 'Actions'].map((h) => (
                                <TableCell
                                    key={h}
                                    align={h === 'Actions' ? 'right' : 'left'}
                                    sx={denseCellSx}
                                >
                                    {h}
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {Array.from({ length: 5 }).map((_, index) => (
                            <TableRow key={index}>
                                {Array.from({ length: 5 }).map((__, cell) => (
                                    <TableCell key={cell} sx={denseCellSx}>
                                        <Skeleton variant="text" height={18} />
                                    </TableCell>
                                ))}
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        );
    }

    if (!users.length) {
        return (
            <Result
                icon={<PeopleOutlined sx={{ fontSize: 32 }} />}
                title="No collaborators yet"
                description="Invite a team member to get started."
            />
        );
    }

    if (isMobile) {
        return (
            <Stack spacing={1}>
                {users.map((user) => (
                    <Paper
                        key={user.id}
                        variant="outlined"
                        sx={{
                            p: 1.5,
                            borderRadius: 1.5,
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 0.75,
                        }}
                    >
                        <Stack
                            direction="row"
                            sx={{
                                justifyContent: 'space-between',
                                alignItems: 'flex-start',
                                gap: 1,
                            }}
                        >
                            <Box sx={{ minWidth: 0 }}>
                                <Typography
                                    variant="body2"
                                    sx={{ fontWeight: 600, lineHeight: 1.35 }}
                                    noWrap
                                >
                                    {getFullName(user)}
                                </Typography>
                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{ wordBreak: 'break-word', display: 'block' }}
                                >
                                    {user.email}
                                </Typography>
                            </Box>
                            <StatusChip status={user.status} />
                        </Stack>

                        <Typography variant="caption" color="text.secondary">
                            Role: {formatRole(user.role)}
                        </Typography>

                        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                            <RowActions user={user} onAction={onAction} />
                        </Box>
                    </Paper>
                ))}
            </Stack>
        );
    }

    return (
        <TableContainer
            component={Paper}
            variant="outlined"
            sx={{ borderRadius: 1.5, overflowX: 'auto' }}
        >
            <Table size="small" sx={{ minWidth: 680, '& .MuiTableCell-root': denseCellSx }}>
                <TableHead>
                    <TableRow>
                        <TableCell sx={{ fontWeight: 700 }}>Name</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Email</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Role</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700, width: 120 }}>
                            Actions
                        </TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {users.map((user) => (
                        <TableRow key={user.id} hover>
                            <TableCell>
                                <Typography
                                    variant="body2"
                                    sx={{ fontWeight: 600, lineHeight: 1.3 }}
                                >
                                    {getFullName(user)}
                                </Typography>
                            </TableCell>
                            <TableCell>
                                <Typography
                                    variant="body2"
                                    sx={{ lineHeight: 1.3, wordBreak: 'break-word' }}
                                >
                                    {user.email}
                                </Typography>
                            </TableCell>
                            <TableCell>
                                <StatusChip status={user.status} />
                            </TableCell>
                            <TableCell>
                                <Tooltip title={formatRole(user.role)}>
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            maxWidth: 160,
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                            lineHeight: 1.3,
                                        }}
                                    >
                                        {formatRole(user.role)}
                                    </Typography>
                                </Tooltip>
                            </TableCell>
                            <TableCell align="right">
                                <Box sx={{ display: 'inline-flex', justifyContent: 'flex-end' }}>
                                    <RowActions user={user} onAction={onAction} />
                                </Box>
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    );
}

export default PermissionsTable;
